// src/components/agency/AgencyDetail.tsx
import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Key,
  ExternalLink,
  Copy,
  Mail,
  Calendar,
  Shield,
  Clock,
  Infinity,
  User,
  Building,
  Phone,
  AtSign,
  Video,
  Image as ImageIcon,
  MessageSquare,
  Link as LinkIcon,
  FileText,
  Palette,
  Instagram,
  Twitter,
  Youtube,
  Edit,
  Save,
  X,
  CreditCard,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { FirebaseService } from '../../services/firebase';
import type { AgencyConfig } from '../../types/agency';
import {
  HERO_VIDEO_OPTIONS,
  VISION_IMAGE_OPTIONS,
  REGISTRATION_METHOD_LABELS,
  REGISTRATION_METHOD_COLORS,
} from '../../constants/agency';

// shadcn/ui components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea'; // プロフィールセクションで使用予定
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AgencyDetailProps {
  agencyId: string;
  onBack: () => void;
  onAgencyUpdated?: () => void;
}

export const AgencyDetail: React.FC<AgencyDetailProps> = ({ agencyId, onBack }) => {
  const [agency, setAgency] = useState<AgencyConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAgency, setEditedAgency] = useState<AgencyConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadAgencyDetails();
  }, [agencyId]);

  const loadAgencyDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const agencyData = await FirebaseService.getAgencyDetails(agencyId);
      if (agencyData) {
        setAgency(agencyData);
      } else {
        setError('代理店が見つかりません');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '代理店詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (agency) {
      setEditedAgency({ ...agency });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedAgency(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!editedAgency) return;

    try {
      setIsSaving(true);
      setError(null);

      const response = await FirebaseService.updateAgency(editedAgency.agencyId, editedAgency);

      if (response.success) {
        await loadAgencyDetails();
        setIsEditing(false);
        setEditedAgency(null);
        alert('代理店情報を更新しました');
      } else {
        setError(response.error || '更新に失敗しました');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const updateEditedField = (field: string, value: any) => {
    if (!editedAgency) return;

    // ネストされたフィールドの更新に対応
    const fields = field.split('.');
    const newAgency = { ...editedAgency };
    let current: any = newAgency;

    for (let i = 0; i < fields.length - 1; i++) {
      if (!current[fields[i]]) {
        current[fields[i]] = {};
      }
      current = current[fields[i]];
    }

    current[fields[fields.length - 1]] = value;
    setEditedAgency(newAgency);
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${type}をクリップボードにコピーしました`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('クリップボードへのコピーに失敗しました');
    }
  };

  const sendByEmail = (accessUrl: string, agencyName: string) => {
    const subject = encodeURIComponent(`【いきいきスマイル共済】アクセスURL - ${agencyName}`);
    const body = encodeURIComponent(
      `
${agencyName} 様

いつもお世話になっております。

いきいきスマイル共済シミュレーターのアクセスURLをお送りします。

【アクセスURL】
${accessUrl}

【有効期限】
無期限

このURLをクリックして、シミュレーターをご利用ください。
セキュリティのため、このURLは他の方との共有はお控えください。

ご不明な点がございましたら、お気軽にお問い合わせください。

よろしくお願いします。
    `.trim()
    );

    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const generateAccessUrl = (shortTokenId: string) => {
    return `https://ikiiki-smile-agency.jp/?id=${shortTokenId}`;
  };

  const generateSimulatorUrl = (shortTokenId: string) => {
    return `https://simulator.ikiiki-smile-agency.jp/access?id=${shortTokenId}`;
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return '未設定';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getVideoLabel = (videoKey: string) => {
    return HERO_VIDEO_OPTIONS.find((v) => v.value === videoKey)?.label || videoKey;
  };

  const getImageLabel = (imageKey: string) => {
    return VISION_IMAGE_OPTIONS.find((i) => i.value === imageKey)?.label || imageKey;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">代理店詳細を読み込んでいます...</span>
        </div>
      </div>
    );
  }

  if (error && !agency) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">エラーが発生しました</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-x-3">
              <Button onClick={loadAgencyDetails}>再試行</Button>
              <Button variant="outline" onClick={onBack}>
                戻る
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!agency) return null;

  const lpUrl = agency.lastShortTokenId ? generateAccessUrl(agency.lastShortTokenId) : null;
  const simulatorUrl = agency.lastShortTokenId ? generateSimulatorUrl(agency.lastShortTokenId) : null;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">代理店詳細</h1>
            <p className="text-sm text-gray-600">ID: {agency.agencyId}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="w-4 h-4 mr-2" />
                キャンセル
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? '保存中...' : '保存'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon" onClick={loadAgencyDetails}>
                <RefreshCw className="w-5 h-5" />
              </Button>
              <Button onClick={handleEdit}>
                <Edit className="w-4 h-4 mr-2" />
                編集
              </Button>
            </>
          )}
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. 基本情報 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Building className="w-5 h-5" />
                <span>基本情報</span>
              </CardTitle>
              {agency.isActive ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  アクティブ
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  無効
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">名前</label>
              {isEditing && editedAgency ? (
                <Input
                  value={editedAgency.agencyName}
                  onChange={(e) => updateEditedField('agencyName', e.target.value)}
                  className="mt-1"
                  placeholder="例: 山田 太郎"
                />
              ) : (
                <p className="text-base font-semibold text-gray-900">{agency.agencyName}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">ステータス</label>
              {isEditing && editedAgency ? (
                <Select
                  value={editedAgency.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => updateEditedField('isActive', value === 'active')}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">アクティブ</SelectItem>
                    <SelectItem value="inactive">無効</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
            </div>

            {agency.registrationMethod && (
              <div>
                <label className="text-sm font-medium text-gray-500">登録方法</label>
                <div className="mt-1">
                  <Badge className={REGISTRATION_METHOD_COLORS[agency.registrationMethod]}>
                    {REGISTRATION_METHOD_LABELS[agency.registrationMethod]}
                  </Badge>
                </div>
              </div>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-gray-500">作成者</label>
                <p className="text-gray-900 flex items-center mt-1">
                  <User className="w-4 h-4 mr-1 text-gray-400" />
                  {agency.createdBy}
                </p>
              </div>
              <div>
                <label className="text-gray-500">作成日時</label>
                <p className="text-gray-900 flex items-center mt-1">
                  <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                  {formatDateTime(agency.createdAt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. 連絡先情報 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Phone className="w-5 h-5" />
              <span>連絡先情報</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">メールアドレス</label>
              {isEditing && editedAgency ? (
                <Input
                  type="email"
                  value={editedAgency.email || ''}
                  onChange={(e) => updateEditedField('email', e.target.value)}
                  className="mt-1"
                  placeholder="example@email.com"
                />
              ) : agency.email ? (
                <p className="text-base text-gray-900 flex items-center">
                  <AtSign className="w-4 h-4 mr-2 text-gray-400" />
                  {agency.email}
                </p>
              ) : (
                <p className="text-sm text-gray-400">未設定</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">電話番号</label>
              {isEditing && editedAgency ? (
                <Input
                  type="tel"
                  value={editedAgency.phone || ''}
                  onChange={(e) => updateEditedField('phone', e.target.value)}
                  className="mt-1"
                  placeholder="03-1234-5678"
                />
              ) : agency.phone ? (
                <p className="text-base text-gray-900 flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-400" />
                  {agency.phone}
                </p>
              ) : (
                <p className="text-sm text-gray-400">未設定</p>
              )}
            </div>

            {!agency.email && !agency.phone && <p className="text-sm text-gray-500">連絡先情報が登録されていません</p>}
          </CardContent>
        </Card>

        {/* 3. LPカスタマイズ設定 */}
        {agency.lpConfig && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Palette className="w-5 h-5" />
                <span>LPカスタマイズ設定</span>
              </CardTitle>
              <CardDescription>ランディングページの表示内容</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ヒーローセクション */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center">
                    <Video className="w-4 h-4 mr-2" />
                    ヒーローセクション
                  </h3>

                  <div>
                    <label className="text-sm font-medium text-gray-500">動画</label>
                    {isEditing && editedAgency?.lpConfig?.hero ? (
                      <Select
                        value={editedAgency.lpConfig.hero.videoKey}
                        onValueChange={(value) => updateEditedField('lpConfig.hero.videoKey', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HERO_VIDEO_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-base text-gray-900">{getVideoLabel(agency.lpConfig.hero.videoKey)}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">タイトル</label>
                    {isEditing && editedAgency?.lpConfig?.hero ? (
                      <Textarea
                        value={editedAgency.lpConfig.hero.title}
                        onChange={(e) => updateEditedField('lpConfig.hero.title', e.target.value)}
                        className="mt-1"
                        placeholder="ヒーロータイトルを入力（改行可能）"
                        rows={3}
                      />
                    ) : (
                      <p className="text-base text-gray-900 whitespace-pre-wrap">{agency.lpConfig.hero.title}</p>
                    )}
                  </div>
                </div>

                {/* ビジョンセクション */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    ビジョンセクション
                  </h3>

                  <div>
                    <label className="text-sm font-medium text-gray-500">画像</label>
                    {isEditing && editedAgency?.lpConfig?.vision ? (
                      <Select
                        value={editedAgency.lpConfig.vision.imageKey}
                        onValueChange={(value) => updateEditedField('lpConfig.vision.imageKey', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VISION_IMAGE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-base text-gray-900">{getImageLabel(agency.lpConfig.vision.imageKey)}</p>
                    )}
                  </div>
                </div>

                {/* CTAボタン */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    CTAボタン
                  </h3>

                  <div>
                    <label className="text-sm font-medium text-gray-500">ボタンテキスト</label>
                    {isEditing && editedAgency?.lpConfig?.cta ? (
                      <Input
                        value={editedAgency.lpConfig.cta.text}
                        onChange={(e) => updateEditedField('lpConfig.cta.text', e.target.value)}
                        className="mt-1"
                        placeholder="CTAボタンテキストを入力"
                      />
                    ) : (
                      <p className="text-base text-gray-900">{agency.lpConfig.cta.text}</p>
                    )}
                  </div>

                  {simulatorUrl && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">リンク先URL</label>
                      <p className="text-sm text-blue-600 truncate">{simulatorUrl}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4. プロフィール設定 */}
        {agency.lpConfig?.profile && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>プロフィール設定</span>
              </CardTitle>
              <CardDescription>代理店担当者のプロフィール</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-6">
                {/* プロフィール画像 */}
                <div className="flex-shrink-0">
                  <Avatar className="w-32 h-32">
                    {agency.lpConfig.profile.photoUrl ? (
                      <AvatarImage src={agency.lpConfig.profile.photoUrl} alt={agency.lpConfig.profile.name} />
                    ) : null}
                    <AvatarFallback className="text-2xl">{agency.lpConfig.profile.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                </div>

                {/* プロフィール情報 */}
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">名前</label>
                    {isEditing && editedAgency?.lpConfig?.profile ? (
                      <Input
                        value={editedAgency.lpConfig.profile.name}
                        onChange={(e) => updateEditedField('lpConfig.profile.name', e.target.value)}
                        className="mt-1"
                        placeholder="名前を入力"
                      />
                    ) : (
                      <p className="text-lg font-semibold text-gray-900">{agency.lpConfig.profile.name}</p>
                    )}
                  </div>

                  {isEditing && editedAgency?.lpConfig?.profile && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">プロフィール画像URL</label>
                      <Input
                        value={editedAgency.lpConfig.profile.photoUrl}
                        onChange={(e) => updateEditedField('lpConfig.profile.photoUrl', e.target.value)}
                        className="mt-1"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-500">紹介文</label>
                    {isEditing && editedAgency?.lpConfig?.profile ? (
                      <Textarea
                        value={editedAgency.lpConfig.profile.bio}
                        onChange={(e) => updateEditedField('lpConfig.profile.bio', e.target.value)}
                        className="mt-1"
                        placeholder="プロフィール紹介文を入力"
                        rows={5}
                      />
                    ) : (
                      <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed break-words overflow-wrap-anywhere">
                        {agency.lpConfig?.profile?.bio}
                      </p>
                    )}
                  </div>

                  {/* SNSリンク */}
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-2">SNSリンク</label>
                    {isEditing && editedAgency?.lpConfig?.profile ? (
                      <div className="space-y-2">
                        <Input
                          value={editedAgency.lpConfig.profile.socialLinks.instagram}
                          onChange={(e) => updateEditedField('lpConfig.profile.socialLinks.instagram', e.target.value)}
                          placeholder="Instagram URL"
                        />
                        <Input
                          value={editedAgency.lpConfig.profile.socialLinks.twitter}
                          onChange={(e) => updateEditedField('lpConfig.profile.socialLinks.twitter', e.target.value)}
                          placeholder="X (Twitter) URL"
                        />
                        <Input
                          value={editedAgency.lpConfig.profile.socialLinks.youtube}
                          onChange={(e) => updateEditedField('lpConfig.profile.socialLinks.youtube', e.target.value)}
                          placeholder="YouTube URL"
                        />
                      </div>
                    ) : agency.lpConfig.profile.socialLinks.instagram ||
                      agency.lpConfig.profile.socialLinks.twitter ||
                      agency.lpConfig.profile.socialLinks.youtube ? (
                      <div className="flex flex-wrap gap-2">
                        {agency.lpConfig.profile.socialLinks.instagram && (
                          <a
                            href={agency.lpConfig.profile.socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-pink-50 text-pink-700 rounded-md hover:bg-pink-100 text-sm"
                          >
                            <Instagram className="w-4 h-4 mr-1" />
                            Instagram
                          </a>
                        )}
                        {agency.lpConfig.profile.socialLinks.twitter && (
                          <a
                            href={agency.lpConfig.profile.socialLinks.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                          >
                            <Twitter className="w-4 h-4 mr-1" />X (Twitter)
                          </a>
                        )}
                        {agency.lpConfig.profile.socialLinks.youtube && (
                          <a
                            href={agency.lpConfig.profile.socialLinks.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-sm"
                          >
                            <Youtube className="w-4 h-4 mr-1" />
                            YouTube
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">未設定</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 5. シミュレーターページ設定 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="w-5 h-5" />
              <span>シミュレーターページ設定</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">LINE URL</label>
              {isEditing && editedAgency?.lpConfig?.contacts ? (
                <Input
                  value={editedAgency.lpConfig.contacts.lineUrl}
                  onChange={(e) => updateEditedField('lpConfig.contacts.lineUrl', e.target.value)}
                  className="mt-1"
                  placeholder="https://line.me/..."
                />
              ) : agency.lpConfig?.contacts?.lineUrl ? (
                <a
                  href={agency.lpConfig.contacts.lineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-600 hover:text-green-700 flex items-center mt-1"
                >
                  <LinkIcon className="w-4 h-4 mr-1" />
                  LINEページを開く
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              ) : (
                <p className="text-sm text-gray-400">未設定</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">マインクラブへのリンク</label>
              {isEditing && editedAgency?.lpConfig?.contacts ? (
                <Input
                  value={editedAgency.lpConfig.contacts.applicationUrl}
                  onChange={(e) => updateEditedField('lpConfig.contacts.applicationUrl', e.target.value)}
                  className="mt-1"
                  placeholder="https://..."
                />
              ) : agency.lpConfig?.contacts?.applicationUrl ? (
                <a
                  href={agency.lpConfig.contacts.applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center mt-1"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  マインクラブを開く
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              ) : (
                <p className="text-sm text-gray-400">未設定</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 6. アクセストークン情報 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5" />
              <span>アクセストークン情報</span>
            </CardTitle>
            <CardDescription>代理店専用のアクセスURL</CardDescription>
          </CardHeader>
          <CardContent>
            {agency.lastShortTokenId && lpUrl ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">現在のアクセストークン</h3>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <Infinity className="w-3 h-3 mr-1" />
                    無期限
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-blue-700 mb-1 block">短縮ID</label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-sm font-mono text-blue-800 bg-white px-3 py-2 rounded border">
                        {agency.lastShortTokenId}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(agency.lastShortTokenId!, '短縮ID')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-blue-700 mb-1 block">LP URL</label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-sm font-mono text-blue-800 bg-white px-3 py-2 rounded border break-all">
                        {lpUrl}
                      </code>
                      <div className="flex items-center space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard(lpUrl, 'LP URL')}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => sendByEmail(lpUrl, agency.agencyName)}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => window.open(lpUrl, '_blank')}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {simulatorUrl && (
                    <div>
                      <label className="text-sm text-blue-700 mb-1 block">シミュレーターURL</label>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 text-sm font-mono text-blue-800 bg-white px-3 py-2 rounded border break-all">
                          {simulatorUrl}
                        </code>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyToClipboard(simulatorUrl, 'シミュレーターURL')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => window.open(simulatorUrl, '_blank')}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {agency.lastTokenGenerated && (
                    <div className="text-sm text-blue-600 flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>生成日時: {formatDateTime(agency.lastTokenGenerated)}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <h3 className="font-semibold text-yellow-800 mb-1">アクセストークン未生成</h3>
                <p className="text-sm text-yellow-700">この代理店にはまだアクセストークンが発行されていません。</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 7. 課金情報 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CreditCard className="w-5 h-5" />
              <span>課金情報</span>
            </CardTitle>
            <CardDescription>Stripeサブスクリプション情報</CardDescription>
          </CardHeader>
          <CardContent>
            {agency.stripeCustomerId ? (
              <div className="space-y-4">
                {/* ステータスバッジ */}
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-500">ステータス:</span>
                  {getSubscriptionStatusBadge(agency.subscriptionStatus, agency.cancelAtPeriodEnd)}
                </div>

                {/* 詳細情報 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Stripe顧客ID</label>
                    <p className="text-sm font-mono text-gray-900 mt-1">{agency.stripeCustomerId}</p>
                  </div>
                  {agency.stripeSubscriptionId && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">サブスクリプションID</label>
                      <p className="text-sm font-mono text-gray-900 mt-1">{agency.stripeSubscriptionId}</p>
                    </div>
                  )}
                </div>

                {/* 解約予定情報 */}
                {(agency.cancelAtPeriodEnd || agency.subscriptionStatus === 'canceling') && agency.cancelAt && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-semibold text-yellow-800">解約予定</h4>
                    </div>
                    <p className="text-sm text-yellow-700">
                      解約予定日: {formatDateTime(agency.cancelAt)}
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      この日以降、LP・シミュレーターへのアクセスができなくなります。
                    </p>
                  </div>
                )}

                {/* 解約済み情報 */}
                {agency.subscriptionStatus === 'canceled' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-red-800">解約済み</h4>
                    </div>
                    {agency.canceledAt && (
                      <p className="text-sm text-red-700">
                        解約日: {formatDateTime(agency.canceledAt)}
                      </p>
                    )}
                    <p className="text-xs text-red-600 mt-1">
                      この代理店のLP・シミュレーターは現在アクセスできません。
                    </p>
                  </div>
                )}

                {/* Stripeダッシュボードへのリンク */}
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://dashboard.stripe.com/customers/${agency.stripeCustomerId}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Stripeダッシュボードで確認
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <h3 className="font-semibold text-gray-700 mb-1">課金情報なし</h3>
                <p className="text-sm text-gray-500">この代理店はセルフ登録（Stripe決済）を行っていません。</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// 課金ステータスの表示用ヘルパー
function getSubscriptionStatusBadge(status?: string, cancelAtPeriodEnd?: boolean) {
  if (!status) {
    return (
      <Badge variant="secondary">
        未登録
      </Badge>
    );
  }

  if (status === 'active' && cancelAtPeriodEnd) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
        <AlertTriangle className="w-3 h-3 mr-1" />
        解約予定
      </Badge>
    );
  }

  switch (status) {
    case 'active':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <CreditCard className="w-3 h-3 mr-1" />
          有効
        </Badge>
      );
    case 'canceling':
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          <AlertTriangle className="w-3 h-3 mr-1" />
          解約予定
        </Badge>
      );
    case 'canceled':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          <XCircle className="w-3 h-3 mr-1" />
          解約済み
        </Badge>
      );
    case 'past_due':
      return (
        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
          <AlertCircle className="w-3 h-3 mr-1" />
          支払い遅延
        </Badge>
      );
    case 'unpaid':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          <AlertCircle className="w-3 h-3 mr-1" />
          未払い
        </Badge>
      );
    case 'trialing':
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Clock className="w-3 h-3 mr-1" />
          トライアル
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          {status}
        </Badge>
      );
  }
}
